import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { getAllTenantFieldOptions } from "../../../../actions/Tenants/actions";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import { usePagination } from "../../../../hooks";
import { ImportExportModal } from "../../../Helper/ImportExportModal/index";
import { MailRecipients } from "../../Helpers";
import { PageBase } from "../PageBase";
import { getEmailRecipients, getTableRow, validateImportItems } from "../utils";
import { defineClientFormCreate } from "./defineClientFormCreate";
import { defineClientInfoFormCreate } from "./defineClientInfoFormCreate";
import { Client } from "./types";
import { useImportExport } from "../../../Helper/ImportExportModal/useImportExport";

const endpoint = "/client/";

const ClientPage = () => {
    const dispatch = useDispatch();
    const config = useConfig();
    const queryClient = useQueryClient();

    const { offset, limit } = usePagination();

    const importFields = [
        { label: "name", description: "Client name", required: true },
        { label: "label", required: true, copyValueFrom: "name" },
        {
            label: "countries",
            description: <span>Multiple values separated by comma. Only countries configured in <Link to={`/tenants/${config?.tenant_id}/form-field-options/country`}>country form options</Link> are valid.</span>,
            required: false,
        },
        {
            label: "fruit_types",
            description: <span>Only fruit types configured in <Link to={`/tenants/${config?.tenant_id}/form-field-options/fruit_type`}>fruit type form options</Link> are valid. Multiple values separated by comma.</span>,
            required: true,
        },
        { label: "report_type", description: "Report types (multiple values separated by comma). Leaving it empty will send all report types." },
        { label: "emails", description: "Emails (multiple values separated by comma)." },
    ];

    const entityName = "client";
    const modalProps = useImportExport({
        entityName,
        importFields,
        validateImportItems: ({ importData, existingItems }) => validateImportItems(entityName, { importData, existingItems, config }),

    });

    const getClients = async (offset, limit) => {
        const res = await callAvosApi(`${endpoint}?limit=${limit}&offset=${offset}`);
        return res.data;
    };

    const { data: clients, isLoading } = useQuery({
        queryKey: ["clients", offset, limit],
        queryFn: () => getClients(offset, limit),
    });

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: ["clients"], exact: false });
        queryClient.invalidateQueries({ queryKey: ["all-clients"], exact: false });
    };

    const { mutate: saveClient } = useMutation({
        mutationFn: async (client: Client) => {
            return callAvosApi(`${endpoint}${client.id ?? ""}`, {
                method: client.id ? "PUT" : "POST",
                data: client,
            });
        },
        onSuccess: () => {
            invalidateQueries();
        },
    });

    const { mutate: deleteClient } = useMutation({
        mutationFn: async (id: string) => {
            return callAvosApi(`${endpoint}${id}`, {
                method: "DELETE",
            });
        },
        onSuccess: () => {
            invalidateQueries();
        },
    });

    const onSaveClient = async (client: Client) => {
        await saveClient(client);
        dispatch(getAllTenantFieldOptions(config.tenant_id) as any);
    };

    const onDeleteClient = async (id: string) => {
        await deleteClient(id);
        dispatch(getAllTenantFieldOptions(config.tenant_id) as any);
    };

    const clientState = useMemo(() => {
        return {
            items: clients || { results: [], count: 0 },
            isLoading,
            filter: { offset, limit },
            localFiltering: false,
            reload: false
        };
    }, [clients, isLoading, offset, limit]);

    const tableRow = [
        ...getTableRow(config, "Client"),
        {
            label: "Email Recipients",
            name: "email_recipients",
            display_component(entity: Client) {
                const email_recipients = getEmailRecipients(entity);
                return <MailRecipients recipients={email_recipients} />;
            },
            lg: 3,
        }
    ];

    const createClientForm = defineClientFormCreate(onSaveClient, onDeleteClient);
    const createClientInfoForm = defineClientInfoFormCreate(onSaveClient);
    const createForms = [createClientInfoForm, createClientForm];
    const defaultModalIndex = createForms.indexOf(createClientForm);

    return <PageBase<Client>
        title="Client"
        tableColumns={tableRow}
        actionsColumnWidth={3}
        createForms={createForms as any}
        state={clientState}
        defaultModalIndex={defaultModalIndex}
        actions={<ImportExportModal entityName="client" importFields={importFields} {...modalProps} />}
    />;
};

export default ClientPage;
